import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Intervention,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import {
  isForbiddenError,
  isUpgradePlanRequiredError,
} from '@lib/utils/graphQLErrors';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import TabBar from '@lib/components/TabBar/TabBar';
import TabsWrap from '@lib/layouts/AppLayout/TabsWrap/TabsWrap';
import getTabs from 'routes/Interventions/utils/getTabs';
import InterventionList from 'routes/Interventions/InterventionList';
import InterventionAssigneesModal from 'components/InterventionAssigneesModal/InterventionAssigneesModal';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { IconName } from '@lib/components/Modal/enums';
import FeatureLockState from '@lib/components/FeatureLockState';
import { PlanState } from '@lib/enums/plan';
import useIsOnline from '@lib/hooks/useIsOnline';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import isErrorTimeoutError from '@lib/utils/isErrorTimeoutError';
import ScanQrStickyBtn from 'components/ScanQrStickyBtn/ScanQrStickyBtn';
import { ScannerActionType } from 'routes/Scanner/enums';
import { APP_URLS } from 'constants/urls';
import useModalManagement from './utils/useModalManagement';
import useActions from './utils/useActions';

export default function InterventionsContainer() {
  const { t } = useTranslation();
  const isOnline = useIsOnline();

  // During navigate() to another route, `use-query-params` re-renders this
  // container with the destination route's URL params *before* React Router
  // commits the unmount (its setState-driven context lags one render).
  // `useMatch` / `useLocation` lag too, but `use-query-params` reads
  // `window.location` directly via its ReactRouter6Adapter, so the in-flight
  // variables already reflect the new URL while the React Router match is
  // still stale. Read `window.location.pathname` here so the gate stays in
  // sync with `useQueryParams`, and the query is skipped during the
  // transition render (e.g. Scanner "Find documents" navigating to
  // /documentation which sets ordering_field=updatedAt — not a valid
  // InterventionSortInputObject field).
  const isRouteMatched =
    typeof window !== 'undefined' &&
    window.location.pathname === APP_URLS.app.interventions.index.path;

  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    handleOpenPlanModal,
  }: ContextProps = useOutletContext();
  // Cache-derived: `whoami` can lag a plan change made in another session
  // (e.g. SystemAdmin disabling the feature mid-session), so this can still
  // read `false` for a moment after the company plan was actually locked.
  const isAccessLocked =
    getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.LimitedRead;
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.Read;

  const useBulkSelectResult = useTableItemsBulkSelect<Intervention>();
  const useModalManagementResult = useModalManagement();
  const {
    closeAllModals,
    isInterventionAssigneesModalOpened,
    openInterventionAssigneesModal,
  } = useModalManagementResult;

  const useInterventionActionsResult = useActions({
    isViewLocked: isAccessLocked,
    // Offline: never fire the query — there's no local cache for
    // interventions like there is for Tasks, so this view is locked instead.
    skip: !isRouteMatched || !isOnline,
    useBulkSelectResult,
    useModalManagementResult,
  });
  const {
    interventionCloseLoading,
    interventionDestroyLoading,
    interventionsQueryHook,
    onConfirmedInterventionClose,
    onConfirmedInterventionDestroy,
    onTabChange,
    tabParam,
  } = useInterventionActionsResult;

  // A FORBIDDEN_ERROR (or UPGRADE_PLAN_REQUIRED) on this query means the
  // access check above raced a plan change and read stale data — treat it as
  // locked instead of falling through to the generic error page below.
  const isViewLocked =
    isAccessLocked ||
    isForbiddenError(interventionsQueryHook.error) ||
    isUpgradePlanRequiredError(interventionsQueryHook.error);

  const tableTabs = getTabs({
    t,
    interventionsCounter: interventionsQueryHook.counters,
  });
  const { totalCount } = interventionsQueryHook;
  const bulkSelectedItemsCount = totalCount
    ? useBulkSelectResult.getSelectedItemsCount(totalCount)
    : 0;

  if (!isOnline || isErrorTimeoutError(interventionsQueryHook.error)) {
    return <ErrorPage status={ErrorPageStatusEnum.Status503} />;
  }

  if (interventionsQueryHook.error && !isViewLocked) {
    return (
      <ErrorPage
        status={getStatusFromError(interventionsQueryHook.error)}
        description={getDescriptionFromError(interventionsQueryHook.error)}
      />
    );
  }

  if (isViewLocked) {
    return (
      <PageBody withoutHeader>
        <FeatureLockState
          title={t('lock-state-title-interventions')}
          text={<Trans i18nKey="lock-state-text-interventions" />}
          onUpgrade={() =>
            handleOpenPlanModal(PlanState.Locked, [
              CompanyPlanUpgradeFeatureEnum.Interventions,
            ])
          }
        />
      </PageBody>
    );
  }

  return (
    <>
      <PageBody>
        <TabsWrap useAppContentMaxWidth>
          <TabBar
            items={tableTabs}
            active={tabParam as string}
            onActiveChange={onTabChange}
            withoutCrop
            variant="fullWidth"
          />
        </TabsWrap>
        <AppContentMaxWidth>
          <InterventionList
            useInterventionActionsResult={useInterventionActionsResult}
            openInterventionAssigneesModal={openInterventionAssigneesModal}
            isViewOnly={isViewOnly}
          />
        </AppContentMaxWidth>
        <ScanQrStickyBtn
          scannerActionType={ScannerActionType.FindInterventions}
        />
      </PageBody>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="intervention-delete-text" />}
        icon={IconName.Delete}
        isOpen={useModalManagementResult.isInterventionDestroyModalOpened}
        loading={interventionDestroyLoading}
        onClose={useModalManagementResult.closeAllModals}
        onConfirm={onConfirmedInterventionDestroy}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-close')}
        description={
          <Trans
            i18nKey={
              bulkSelectedItemsCount > 1
                ? 'interventions-close-text'
                : 'intervention-close-text'
            }
          />
        }
        icon={IconName.Warning}
        isOpen={useModalManagementResult.isInterventionCloseModalOpened}
        loading={interventionCloseLoading}
        onClose={useModalManagementResult.closeAllModals}
        onConfirm={onConfirmedInterventionClose}
        title={t('are-you-sure')}
        useProgress
      />
      <InterventionAssigneesModal
        caption={t('all-assignees')}
        interventionId={isInterventionAssigneesModalOpened?.id}
        isOpen={!!isInterventionAssigneesModalOpened?.id}
        onClose={closeAllModals}
      />
    </>
  );
}
